import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../utils/time';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_BADGE = {
  pending:    'bg-amber-100 text-amber-700',
  approved:   'bg-blue-100 text-blue-700',
  reimbursed: 'bg-emerald-100 text-emerald-700',
  rejected:   'bg-red-100 text-red-700',
};

const CategoryTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-800 border border-white/10 rounded-xl p-3 shadow-xl text-xs">
      <p className="text-white font-semibold">{d.category}</p>
      <p className="text-slate-300">{d.count} claim{d.count !== 1 ? 's' : ''}</p>
      <p className="text-emerald-400 font-bold">{fmt(d.total)}</p>
    </div>
  );
};

export default function ReimbursementsReport() {
  const [data, setData] = useState({ by_status: [], by_category: [], by_staff: [], claims: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [from, setFrom] = useState(() => isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(() => isoDate(today));

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/reimbursements/summary?from=${from}&to=${to}`);
      setData(r.data || { by_status: [], by_category: [], by_staff: [], claims: [], totals: {} });
    } catch { toast.error('Failed to load reimbursements report'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [from, to]);

  const t = data.totals || {};
  const pieData = (data.by_category || []).map(c => ({
    category: c.category || 'Other',
    total: parseFloat(c.total || 0),
    count: parseInt(c.count || 0),
  }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reimbursements Report</h1>
          <p className="text-sm text-slate-500">Staff expense claims by status, category and worker</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          <span className="text-slate-400 text-sm">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          <button onClick={load} disabled={loading}
            className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-50 border border-slate-100 rounded-2xl px-5 py-4">
          <p className="text-sm text-slate-600">Total Claimed</p>
          <p className="text-xl font-bold text-slate-800">{fmt(t.total)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{t.count || 0} claim{(t.count || 0) !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4">
          <p className="text-sm text-slate-600">Pending</p>
          <p className="text-xl font-bold text-amber-700">{fmt(t.pending_total)}</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-4">
          <p className="text-sm text-slate-600">Approved (unpaid)</p>
          <p className="text-xl font-bold text-blue-700">{fmt(t.approved_total)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4">
          <p className="text-sm text-slate-600">Reimbursed</p>
          <p className="text-xl font-bold text-emerald-700">{fmt(t.reimbursed_total)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie by category */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">By Category</h2>
          {pieData.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">No claims in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="total" nameKey="category" paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CategoryTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By staff table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100"><h2 className="text-sm font-semibold text-slate-700">By Staff Member</h2></div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3">Staff</th>
                <th className="text-right px-4 py-3">Claims</th>
                <th className="text-right px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data.by_staff || []).length === 0 ? (
                <tr><td colSpan={3} className="text-center py-8 text-slate-400 text-sm">No data</td></tr>
              ) : data.by_staff.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">{row.staff_name}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{row.count}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-800">{fmt(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Claim log */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-100"><h2 className="text-sm font-semibold text-slate-700">Claim Log</h2></div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Staff</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Description</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-10"><div className="inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : (data.claims || []).length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No claims for this period</td></tr>
            ) : data.claims.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500">{row.expense_date?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-slate-700">{row.staff_name}</td>
                <td className="px-4 py-3 text-slate-500"><span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{row.category}</span></td>
                <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate">{row.description || '—'}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[row.status] || 'bg-slate-100 text-slate-600'}`}>{row.status}</span></td>
                <td className="px-4 py-3 text-right font-bold text-slate-800">{fmt(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
